import React from 'react';
import { Crown, Lock, ArrowUpRight } from 'lucide-react';

interface UpgradeRequiredProps {
    feature: string;
    requiredPlan: string;
    message?: string;
    className?: string;
}

export const UpgradeRequired: React.FC<UpgradeRequiredProps> = ({
    feature,
    requiredPlan,
    message,
    className = '',
}) => {
    return (
        <div className={`absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20 ${className}`}>
            <div className="text-center p-6 max-w-sm">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-white/5 flex items-center justify-center">
                    <Lock className="w-6 h-6 text-white/40" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">
                    {feature} Locked
                </h3>
                <p className="text-white/50 text-sm mb-4">
                    {message || `This feature requires ${requiredPlan} plan`}
                </p>
                <a
                    href="https://ikcore.cloud/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-black rounded-lg text-sm font-medium hover:bg-white/90 transition-colors"
                >
                    <Crown className="w-4 h-4" />
                    Upgrade to {requiredPlan}
                    <ArrowUpRight className="w-4 h-4" />
                </a>
            </div>
        </div>
    );
};

// Smaller inline version for limit warnings
interface LimitWarningProps {
    resource: 'servers' | 'domains';
    current: number;
    limit: number;
    severity: 'warning' | 'critical';
}

export const LimitWarning: React.FC<LimitWarningProps> = ({
    resource,
    current,
    limit,
    severity,
}) => {
    const isWarning = severity === 'warning';
    const resourceName = resource === 'servers' ? 'Servers' : 'Domains';

    return (
        <div className={`p-3 rounded-lg flex items-center justify-between ${isWarning
            ? 'bg-yellow-500/10 border border-yellow-500/20'
            : 'bg-red-500/10 border border-red-500/20'
            }`}>
            <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isWarning ? 'bg-yellow-500/20' : 'bg-red-500/20'
                    }`}>
                    <span className={`text-sm font-bold ${isWarning ? 'text-yellow-400' : 'text-red-400'}`}>
                        {current}/{limit}
                    </span>
                </div>
                <div>
                    <p className={`text-sm font-medium ${isWarning ? 'text-yellow-400' : 'text-red-400'}`}>
                        {isWarning ? `${resourceName} limit almost reached` : `${resourceName} limit reached`}
                    </p>
                    <p className="text-xs text-white/40">
                        {isWarning
                            ? `You have ${limit - current} ${resource} remaining`
                            : 'Upgrade your plan to add more'
                        }
                    </p>
                </div>
            </div>
            <a
                href="https://ikcore.cloud/pricing"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-xs text-white/70 hover:text-white border border-white/10 rounded-lg hover:border-white/20 transition-colors"
            >
                Upgrade
            </a>
        </div>
    );
};

// Tab lock indicator for navigation
export const LockedTabIndicator: React.FC = () => (
    <div className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-white/10 flex items-center justify-center">
        <Lock className="w-2.5 h-2.5 text-white/40" />
    </div>
);

export default UpgradeRequired;
